import React, { useRef, useEffect, useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

// Constants
const CORNER_RADIUS = 8;
const LINE_WIDTH = 2;
const QUAD_COLOR = '#ff9800';
const QUAD_FILL_COLOR = 'rgba(255, 152, 0, 0.2)';
const CORNER_COLOR = '#ff9800';
const CORNER_ACTIVE_COLOR = '#f57c00';
const MIN_ZOOM = 0.25;
const MAX_ZOOM = 4;
const ZOOM_SENSITIVITY = 0.001;

// Corner point structure for internal use
interface Point {
  x: number;
  y: number;
}

// Convert API format [[x1,x2,x3,x4], [y1,y2,y3,y4]] to points [{x,y}, ...]
const apiCornersToPoints = (corners: number[][]): Point[] => {
  const xValues = corners[0];
  const yValues = corners[1];
  return [
    { x: xValues[0], y: yValues[0] }, // top-left
    { x: xValues[1], y: yValues[1] }, // top-right
    { x: xValues[2], y: yValues[2] }, // bottom-right
    { x: xValues[3], y: yValues[3] }, // bottom-left
  ];
};

// Convert points [{x,y}, ...] back to API format [[x1,x2,x3,x4], [y1,y2,y3,y4]]
const pointsToApiCorners = (points: Point[]): number[][] => {
  return [
    points.map((p) => Math.round(p.x)),
    points.map((p) => Math.round(p.y)),
  ];
};

interface PlateAlignmentEditorProps {
  open: boolean;
  plateId: string;
  imageUrl: string;
  initialCorners: number[][];
  onClose: () => void;
  onCornersUpdate: (plateId: string, corners: number[][]) => Promise<void>;
}

const PlateAlignmentEditor: React.FC<PlateAlignmentEditorProps> = ({
  open,
  plateId,
  imageUrl,
  initialCorners,
  onClose,
  onCornersUpdate,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Image state
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Transform state
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });

  // Corners state as points for easier manipulation
  const [corners, setCorners] = useState<Point[]>(() => apiCornersToPoints(initialCorners));

  // Interaction state
  const [draggingCorner, setDraggingCorner] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Track if dialog was previously open to detect fresh opens
  const wasOpen = useRef(false);

  // Reset state only when dialog first opens (not when corners update)
  useEffect(() => {
    if (open && !wasOpen.current) {
      // Dialog just opened
      setCorners(apiCornersToPoints(initialCorners));
      setZoom(1);
      setOffset({ x: 0, y: 0 });
      setError(null);
      setHasChanges(false);
    }
    wasOpen.current = open;
  }, [open, initialCorners]);

  // Load image
  useEffect(() => {
    if (!open) return;

    setLoading(true);
    setError(null);

    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      setImage(img);
      setLoading(false);

      // Fill entire canvas with image (may crop edges)
      if (containerRef.current) {
        const containerWidth = containerRef.current.clientWidth;
        const containerHeight = containerRef.current.clientHeight;
        // Use Math.max to fill entire space (instead of Math.min which fits within)
        const scale = Math.max(
          containerWidth / img.width,
          containerHeight / img.height
        );
        setZoom(scale);
        setOffset({
          x: (containerWidth - img.width * scale) / 2,
          y: (containerHeight - img.height * scale) / 2,
        });
      }
    };

    img.onerror = () => {
      setError('Failed to load image');
      setLoading(false);
    };

    img.src = imageUrl;

    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [open, imageUrl]);

  // Convert screen coordinates to image coordinates
  const screenToImage = useCallback(
    (screenX: number, screenY: number) => {
      const canvas = canvasRef.current;
      if (!canvas) return { x: 0, y: 0 };

      const rect = canvas.getBoundingClientRect();
      const x = (screenX - rect.left - offset.x) / zoom;
      const y = (screenY - rect.top - offset.y) / zoom;
      return { x, y };
    },
    [zoom, offset]
  );

  // Convert image coordinates to screen coordinates
  const imageToScreen = useCallback(
    (imageX: number, imageY: number) => {
      return {
        x: imageX * zoom + offset.x,
        y: imageY * zoom + offset.y,
      };
    },
    [zoom, offset]
  );

  // Find the closest corner to the given screen position
  const findClosestCorner = useCallback(
    (screenX: number, screenY: number): number => {
      const canvas = canvasRef.current;
      if (!canvas) return 0;

      const rect = canvas.getBoundingClientRect();
      const canvasX = screenX - rect.left;
      const canvasY = screenY - rect.top;

      let closestIndex = 0;
      let closestDist = Infinity;

      for (let i = 0; i < corners.length; i++) {
        const corner = corners[i];
        const screenPos = imageToScreen(corner.x, corner.y);
        const dx = canvasX - screenPos.x;
        const dy = canvasY - screenPos.y;
        const dist = Math.sqrt(dx * dx + dy * dy);

        if (dist < closestDist) {
          closestDist = dist;
          closestIndex = i;
        }
      }

      return closestIndex;
    },
    [corners, imageToScreen]
  );

  // Draw canvas
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx || !image) return;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw image
    ctx.save();
    ctx.translate(offset.x, offset.y);
    ctx.scale(zoom, zoom);
    ctx.drawImage(image, 0, 0);
    ctx.restore();

    // Draw quadrilateral fill
    ctx.save();
    ctx.beginPath();
    const firstCorner = imageToScreen(corners[0].x, corners[0].y);
    ctx.moveTo(firstCorner.x, firstCorner.y);
    for (let i = 1; i < corners.length; i++) {
      const screenCorner = imageToScreen(corners[i].x, corners[i].y);
      ctx.lineTo(screenCorner.x, screenCorner.y);
    }
    ctx.closePath();
    ctx.fillStyle = QUAD_FILL_COLOR;
    ctx.fill();
    ctx.restore();

    // Draw quadrilateral outline
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(firstCorner.x, firstCorner.y);
    for (let i = 1; i < corners.length; i++) {
      const screenCorner = imageToScreen(corners[i].x, corners[i].y);
      ctx.lineTo(screenCorner.x, screenCorner.y);
    }
    ctx.closePath();
    ctx.strokeStyle = QUAD_COLOR;
    ctx.lineWidth = LINE_WIDTH;
    ctx.stroke();
    ctx.restore();

    // Draw corners
    for (let i = 0; i < corners.length; i++) {
      const screenCorner = imageToScreen(corners[i].x, corners[i].y);
      const isActive = draggingCorner === i;

      ctx.save();
      ctx.beginPath();
      ctx.arc(screenCorner.x, screenCorner.y, CORNER_RADIUS, 0, Math.PI * 2);
      ctx.fillStyle = isActive ? CORNER_ACTIVE_COLOR : CORNER_COLOR;
      ctx.fill();
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 2;
      ctx.stroke();
      ctx.restore();

      // Draw corner number
      ctx.save();
      ctx.fillStyle = '#fff';
      ctx.font = 'bold 10px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(String(i + 1), screenCorner.x, screenCorner.y);
      ctx.restore();
    }
  }, [image, zoom, offset, corners, draggingCorner, imageToScreen]);

  // Redraw when state changes
  useEffect(() => {
    draw();
  }, [draw]);

  // Resize canvas to container
  useEffect(() => {
    const resizeCanvas = () => {
      const canvas = canvasRef.current;
      const container = containerRef.current;
      if (!canvas || !container) return;

      canvas.width = container.clientWidth;
      canvas.height = container.clientHeight;
      draw();
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    return () => window.removeEventListener('resize', resizeCanvas);
  }, [draw, open]);

  // Handle mouse down - always select closest corner
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      const closestCornerIndex = findClosestCorner(e.clientX, e.clientY);
      setDraggingCorner(closestCornerIndex);
    },
    [findClosestCorner]
  );

  // Handle mouse move
  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (draggingCorner !== null) {
        const imagePos = screenToImage(e.clientX, e.clientY);
        setCorners((prev) => {
          const newCorners = [...prev];
          newCorners[draggingCorner] = {
            x: Math.round(imagePos.x),
            y: Math.round(imagePos.y),
          };
          return newCorners;
        });
        setHasChanges(true);
      }
    },
    [draggingCorner, screenToImage]
  );

  // Handle mouse up - deselect corner (save happens on close)
  const handleMouseUp = useCallback(() => {
    if (draggingCorner !== null) {
      setDraggingCorner(null);
    }
  }, [draggingCorner]);

  // Handle mouse leave - deselect corner
  const handleMouseLeave = useCallback(() => {
    if (draggingCorner !== null) {
      setDraggingCorner(null);
    }
  }, [draggingCorner]);

  // Handle close - save changes if any
  const handleClose = useCallback(async () => {
    if (hasChanges) {
      setSaving(true);
      try {
        const apiCorners = pointsToApiCorners(corners);
        await onCornersUpdate(plateId, apiCorners);
      } catch (err) {
        console.error('Failed to update corners:', err);
      } finally {
        setSaving(false);
      }
    }
    onClose();
  }, [hasChanges, corners, plateId, onCornersUpdate, onClose]);

  // Handle wheel for zoom
  const handleWheel = useCallback(
    (e: React.WheelEvent) => {
      e.preventDefault();

      const canvas = canvasRef.current;
      if (!canvas) return;

      const rect = canvas.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      // Calculate new zoom
      const delta = -e.deltaY * ZOOM_SENSITIVITY;
      const newZoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoom * (1 + delta)));

      // Adjust offset to zoom relative to mouse position
      const zoomRatio = newZoom / zoom;
      const newOffsetX = mouseX - (mouseX - offset.x) * zoomRatio;
      const newOffsetY = mouseY - (mouseY - offset.y) * zoomRatio;

      setZoom(newZoom);
      setOffset({ x: newOffsetX, y: newOffsetY });
    },
    [zoom, offset]
  );

  // Zoom controls
  const handleZoomIn = () => {
    const newZoom = Math.min(MAX_ZOOM, zoom * 1.25);
    const canvas = canvasRef.current;
    if (canvas) {
      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;
      const zoomRatio = newZoom / zoom;
      setOffset({
        x: centerX - (centerX - offset.x) * zoomRatio,
        y: centerY - (centerY - offset.y) * zoomRatio,
      });
    }
    setZoom(newZoom);
  };

  const handleZoomOut = () => {
    const newZoom = Math.max(MIN_ZOOM, zoom / 1.25);
    const canvas = canvasRef.current;
    if (canvas) {
      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;
      const zoomRatio = newZoom / zoom;
      setOffset({
        x: centerX - (centerX - offset.x) * zoomRatio,
        y: centerY - (centerY - offset.y) * zoomRatio,
      });
    }
    setZoom(newZoom);
  };

  const handleResetView = () => {
    if (image && containerRef.current) {
      const containerWidth = containerRef.current.clientWidth;
      const containerHeight = containerRef.current.clientHeight;
      // Use Math.max to fill entire space
      const scale = Math.max(
        containerWidth / image.width,
        containerHeight / image.height
      );
      setZoom(scale);
      setOffset({
        x: (containerWidth - image.width * scale) / 2,
        y: (containerHeight - image.height * scale) / 2,
      });
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { height: '80vh' },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h6">Plate Alignment Editor</Typography>
          <Typography variant="caption" color="text.secondary">
            Drag corners to adjust plate boundaries. Use mouse wheel to zoom.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {saving && <CircularProgress size={20} />}
          <IconButton onClick={handleZoomOut} size="small" title="Zoom Out">
            <ZoomOutIcon />
          </IconButton>
          <Typography variant="body2" sx={{ minWidth: 50, textAlign: 'center' }}>
            {Math.round(zoom * 100)}%
          </Typography>
          <IconButton onClick={handleZoomIn} size="small" title="Zoom In">
            <ZoomInIcon />
          </IconButton>
          <IconButton onClick={handleResetView} size="small" title="Reset View">
            <RestartAltIcon />
          </IconButton>
          <IconButton onClick={handleClose} size="small" disabled={saving}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
        <Box
          ref={containerRef}
          sx={{
            width: '100%',
            height: '100%',
            bgcolor: '#1a1a1a',
            position: 'relative',
          }}
        >
          {loading && (
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
              }}
            >
              <CircularProgress />
            </Box>
          )}

          {error && (
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                color: 'error.main',
              }}
            >
              <Typography>{error}</Typography>
            </Box>
          )}

          <canvas
            ref={canvasRef}
            style={{
              display: loading ? 'none' : 'block',
              cursor: draggingCorner !== null ? 'grabbing' : 'crosshair',
            }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
            onWheel={handleWheel}
            role="application"
            aria-label="Plate alignment editor. Click to select a corner, drag to move it. Scroll to zoom."
            tabIndex={0}
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
          Corners: {corners.map((c, i) => `${i + 1}:(${c.x}, ${c.y})`).join(' ')}
          {hasChanges && ' (unsaved)'}
        </Typography>
        <Button onClick={handleClose} disabled={saving}>
          {saving ? 'Saving...' : hasChanges ? 'Save & Close' : 'Close'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PlateAlignmentEditor;
